import {
  EFreightForCarrierStatus,
  EFreightTransportGoodsType,
  EStopStatus,
  IFreightBasicForCarrier,
  IFreightDetailedForCarrier,
  IFreightVehicle,
  IInvoiceReferenceBasic,
  IStopBasicForCarrier,
  IStopDetailedForCarrier,
} from '@okcargo-apps/shared-domain';
import {
  FreightBasicForCarrierResponse,
  FreightDetailForCarrierResponse,
  FreightStopStatus,
} from '@okcargo/query-processor';
import {
  mapFastPaymentStatusEnum,
  mapFreightBillingStatusEnum,
  mapLoadingTypeEnum,
  mapStopStatusEnum,
  mapStopTypeEnum,
  mapTransportGoodsTypeEnum,
  mapVehicleTypeEnum,
  transformToEFreightForCarrierStatus,
} from './freight-carrier-enum-mapper';

export function transformToIFreightBasicForCarrier(
  freightBasicResponse: FreightBasicForCarrierResponse,
  freight: IFreightBasicForCarrier,
): IFreightBasicForCarrier {
  let isAnyStopStartedOrFinished = false;

  freight = {
    ...freight,
    id: freightBasicResponse.id,
    stopsBasic: [],
    numberOfStops: freightBasicResponse.stops.length,
    reference: freightBasicResponse.reference,
    // TODO If the user does not have capabilities, do not add drivers or vehicles ???
    drivers: [],
    vehicles: [],
    price: freightBasicResponse.price ?? undefined,
    hasPendingDoc: freightBasicResponse.hasPendingDoc ?? undefined,
    scheduledKm: freightBasicResponse.scheduledKm ?? undefined,
    deliveryNoteInPaperRequired: freightBasicResponse.deliveryNoteInPaperRequired ?? undefined,
    deliveryNoteInPaperReceived:
      freightBasicResponse.deliveryNoteInPaperRequired === true
        ? (freightBasicResponse.deliveryNoteInPaperReceived ?? false)
        : undefined,
    billingStatus: freightBasicResponse.billingStatus
      ? mapFreightBillingStatusEnum(freightBasicResponse.billingStatus)
      : undefined,
    fastPaymentForCarrierStatus: freightBasicResponse.fastPaymentStatus
      ? mapFastPaymentStatusEnum(freightBasicResponse.fastPaymentStatus)
      : undefined,
    invoiceList: [],
  };

  const orderedStops = [...freightBasicResponse.stops].sort((a, b) => a.position - b.position);

  orderedStops.forEach((stopResponse) => {
    const stop: IStopBasicForCarrier = {
      id: stopResponse.id,
      position: stopResponse.position,
      type: mapStopTypeEnum(stopResponse.type),
      status: mapStopStatusEnum(stopResponse.stopStatus),
      since: new Date(stopResponse.since),
      until: new Date(stopResponse.until),
      address: {
        company: stopResponse.companyName,
        street: stopResponse.street,
        // extraLine: stopResponse.extraLine,
        city: stopResponse.city,
        postalCode: stopResponse.postalCode,
        stateCode: stopResponse.stateCode,
        // state: stopResponse.state, // TODO calculate this
        countryCode: stopResponse.countryCode,
        // country: stopResponse.country, // TODO calculate this
      },
      // reference: stopResponse.reference, // not necessary yet
    };

    freight.stopsBasic.push(stop);

    if (
      stopResponse.stopStatus === FreightStopStatus.STARTED ||
      stopResponse.stopStatus === FreightStopStatus.FINISHED
    ) {
      isAnyStopStartedOrFinished = true;
    }
  });

  const nextStop = freight.stopsBasic.find(
    (stop) => stop.status === EStopStatus.STARTED || stop.status === EStopStatus.UNSTARTED,
  );

  const targetStop = nextStop ?? freight.stopsBasic[freight.stopsBasic.length - 1];

  freight.nextStopId = targetStop.id;
  freight.nextStopType = targetStop.type;
  freight.nextStopStatus = targetStop.status;

  if (freightBasicResponse.status !== undefined) {
    freight.status = transformToEFreightForCarrierStatus(freightBasicResponse.status, isAnyStopStartedOrFinished);

    // It prevents issues if the freight status is inconsistent with the stops status:
    // - If last stop finished but freight not finished, mark freight as finished
    if (
      freight.stopsBasic.length >= 2 &&
      freight.status !== EFreightForCarrierStatus.FINISHED &&
      freight.stopsBasic[freight.stopsBasic.length - 1].status === EStopStatus.FINISHED
    ) {
      freight.status = EFreightForCarrierStatus.FINISHED;
    }
    // - If last stop is not finished but freight is finished, mark all stops as finished
    if (
      freight.stopsBasic.length >= 2 &&
      freight.status === EFreightForCarrierStatus.FINISHED &&
      freight.stopsBasic[freight.stopsBasic.length - 1].status !== EStopStatus.FINISHED
    ) {
      freight.stopsBasic.forEach((stop) => {
        stop.status = EStopStatus.FINISHED;
      });
    }
  } else {
    freight.status = EFreightForCarrierStatus.DEFAULT;
  }

  if (freightBasicResponse.driverId && freightBasicResponse.driverName) {
    freight.drivers.push({
      id: freightBasicResponse.driverId,
      name: freightBasicResponse.driverName,
    });
  }

  freightBasicResponse.vehicles.forEach((vehicleResponse) => {
    const vehicle: IFreightVehicle = {
      id: vehicleResponse.id,
      plate: vehicleResponse.plate,
      // TODO remove the casting when FreightBasicForCarrierVehicle has property `type`
      type: (vehicleResponse as any).type ?? undefined,
    };
    freight.vehicles.push(vehicle);
  });

  if (freightBasicResponse.invoiceList) {
    freightBasicResponse.invoiceList.forEach((invoiceResponse) => {
      const invoce: IInvoiceReferenceBasic = {
        id: invoiceResponse.id,
        number: invoiceResponse.number,
      };
      freight.invoiceList.push(invoce);
    });
  }

  return freight;
}

export function transformToIFreightDetailForCarrier(
  freightDetailedResponse: FreightDetailForCarrierResponse,
  freight: IFreightDetailedForCarrier,
): IFreightDetailedForCarrier {
  let isAnyStopStartedOrFinished = false;

  freight = {
    ...freight,
    id: freightDetailedResponse.id,
    stopsDetailed: [],
    numberOfStops: freightDetailedResponse.freightStopResponses.length,
    reference: freightDetailedResponse.reference,
    // TODO If the user does not have capabilities, do not add drivers or vehicles ???
    drivers: [],
    vehicles: [],
    vehicleTypes: [],
    loadingTypes: [],
    price: freightDetailedResponse.price ?? undefined,
    hasPendingDoc: freightDetailedResponse.hasPendingDoc ?? undefined,
    scheduledKm: freightDetailedResponse.scheduledKm ?? undefined,
    deliveryNoteInPaperRequired: freightDetailedResponse.deliveryNoteInPaperRequired ?? undefined,
    deliveryNoteInPaperReceived:
      freightDetailedResponse.deliveryNoteInPaperRequired === true
        ? (freightDetailedResponse.deliveryNoteInPaperReceived ?? false)
        : undefined,
    billingStatus: freightDetailedResponse.billingStatus
      ? mapFreightBillingStatusEnum(freightDetailedResponse.billingStatus)
      : undefined,
    fastPaymentForCarrierStatus: freightDetailedResponse.fastPaymentStatus
      ? mapFastPaymentStatusEnum(freightDetailedResponse.fastPaymentStatus)
      : undefined,
    invoiceList: [],
    transportGoodsType: mapTransportGoodsTypeEnum(freightDetailedResponse.transportGoodsType),
    waste: false, // is subsequently modified if transportGoodsType === WASTE
    instructions: freightDetailedResponse.instructions ?? '',
    observations: freightDetailedResponse.observations ?? '',
    loadedKm: freightDetailedResponse.loadedKm ?? undefined,
    emptyKm: freightDetailedResponse.emptyKm ?? undefined,
    detourKm: freightDetailedResponse.detourKm ?? undefined,
    loadedPerKmPrice: freightDetailedResponse.loadedPerKmPrice ?? undefined,
    emptyPerKmPrice: freightDetailedResponse.emptyPerKmPrice ?? undefined,
    detourPerKmPrice: freightDetailedResponse.detourPerKmPrice ?? undefined,
    payInAdvanceRequestedAt: freightDetailedResponse.payInAdvanceRequestedAt
      ? new Date(freightDetailedResponse.payInAdvanceRequestedAt)
      : undefined,
    payInAdvanceApprovedAt: freightDetailedResponse.payInAdvanceApprovedAt
      ? new Date(freightDetailedResponse.payInAdvanceApprovedAt)
      : undefined,
    payInAdvanceRejectedAt: freightDetailedResponse.payInAdvanceRejectedAt
      ? new Date(freightDetailedResponse.payInAdvanceRejectedAt)
      : undefined,
    payInAdvanceRejectedReason: freightDetailedResponse.payInAdvanceRejectedReason ?? undefined,
    costDiscount: freightDetailedResponse.costDiscount ?? undefined,
  };

  const orderedStops = [...freightDetailedResponse.freightStopResponses].sort((a, b) => a.position - b.position);

  orderedStops.forEach((stopResponse) => {
    const stop: IStopDetailedForCarrier = {
      id: stopResponse.id,
      // freightId: stopResponse.freightId,
      position: stopResponse.position,
      type: mapStopTypeEnum(stopResponse.type),
      status: stopResponse.arrivedAt
        ? stopResponse.departedAt
          ? EStopStatus.FINISHED
          : EStopStatus.STARTED
        : EStopStatus.UNSTARTED,
      instructions: stopResponse.instructions,
      since: new Date(stopResponse.since),
      until: new Date(stopResponse.until),
      address: {
        company: stopResponse.companyPayload.name,
        phone: stopResponse.companyPayload.phone ?? undefined,
        taxId: stopResponse.companyPayload.taxId ?? undefined,
        street: stopResponse.addressPayload.street,
        extraLine: stopResponse.addressPayload.extraLine ?? undefined,
        city: stopResponse.addressPayload.city,
        postalCode: stopResponse.addressPayload.postalCode,
        // state: stopResponse.addressPayload.state,
        stateCode: stopResponse.addressPayload.stateCode,
        // country: stopResponse.addressPayload.country,
        countryCode: stopResponse.addressPayload.countryCode,
        coords: stopResponse.addressPayload.pointPayload ?? undefined, // map coords?
        addressbookId: stopResponse.addressPayload.id ?? undefined,
      },

      goodsDescription: stopResponse.goodsDescription ?? undefined,
      reference: stopResponse.reference ?? undefined,
      netWeight: stopResponse.netWeight ?? undefined,
      packages: stopResponse.packages ?? undefined,
      unloadedNetWeight: stopResponse.unloadedNetWeight ?? undefined,
      unloadedPackages: stopResponse.unloadedPackages ?? undefined,
      arrivalDriverAppPoint: stopResponse.arrivalDriverAppPoint ?? undefined,
      arrivalTruckPoint: stopResponse.arrivalTruckPoint ?? undefined,
      reservations: stopResponse.reservations ?? undefined,
      acceptanceDriverAppPoint: stopResponse.acceptanceDriverAppPoint ?? undefined,
      acceptanceTruckPoint: stopResponse.acceptanceTruckPoint ?? undefined,
      acceptanceSignatureImage: stopResponse.acceptanceSignatureImage ?? undefined,
      acceptanceName: stopResponse.acceptanceName ?? undefined,
      acceptanceTaxId: stopResponse.acceptanceTaxId ?? undefined,
      acceptanceAcceptedAt: stopResponse.acceptanceAcceptedAt ?? undefined,
      sealNumber: stopResponse.sealNumber ?? undefined,
      arrivedAt: stopResponse.arrivedAt ? new Date(stopResponse.arrivedAt) : undefined,
      departedAt: stopResponse.departedAt ? new Date(stopResponse.departedAt) : undefined,
    };

    if (stopResponse.arrivedAt !== undefined) {
      isAnyStopStartedOrFinished = true;
    }

    freight.stopsDetailed.push(stop);
  });

  const nextStop = freight.stopsDetailed.find(
    (stop) => stop.status === EStopStatus.STARTED || stop.status === EStopStatus.UNSTARTED,
  );

  const targetStop = nextStop ?? freight.stopsDetailed[freight.stopsDetailed.length - 1];

  freight.nextStopId = targetStop.id;
  freight.nextStopType = targetStop.type;
  freight.nextStopStatus = targetStop.status;

  if (freightDetailedResponse.status !== undefined) {
    freight.status = transformToEFreightForCarrierStatus(freightDetailedResponse.status, isAnyStopStartedOrFinished);

    // It prevents issues if the freight status is inconsistent with the stops status:
    // - If last stop finished but freight not finished, mark freight as finished
    if (
      freight.stopsDetailed.length >= 2 &&
      freight.status !== EFreightForCarrierStatus.FINISHED &&
      freight.stopsDetailed[freight.stopsDetailed.length - 1].status === EStopStatus.FINISHED
    ) {
      freight.status = EFreightForCarrierStatus.FINISHED;
    }
    // - If last stop is not finished but freight is finished, mark all stops as finished
    if (
      freight.stopsDetailed.length >= 2 &&
      freight.status === EFreightForCarrierStatus.FINISHED &&
      freight.stopsDetailed[freight.stopsDetailed.length - 1].status !== EStopStatus.FINISHED
    ) {
      freight.stopsDetailed.forEach((stop) => {
        stop.status = EStopStatus.FINISHED;
      });
    }
  } else {
    freight.status = EFreightForCarrierStatus.DEFAULT;
  }

  if (freightDetailedResponse.driverId && freightDetailedResponse.driverName) {
    freight.drivers.push({
      id: freightDetailedResponse.driverId,
      name: freightDetailedResponse.driverName,
    });
  }

  freightDetailedResponse.vehicles.forEach((vehicleResponse) => {
    const vehicle: IFreightVehicle = {
      id: vehicleResponse.id,
      plate: vehicleResponse.plate,
    };
    freight.vehicles.push(vehicle);
  });

  freightDetailedResponse.invoiceList.forEach((invoiceResponse) => {
    const invoce: IInvoiceReferenceBasic = {
      id: invoiceResponse.id,
      number: invoiceResponse.number,
    };
    freight.invoiceList.push(invoce);
  });

  freightDetailedResponse.vehicleTypes.forEach((vehicleTypeResponse) => {
    const vehicleType = mapVehicleTypeEnum(vehicleTypeResponse);
    freight.vehicleTypes.push(vehicleType);
  });

  freightDetailedResponse.loadingTypes.forEach((loadingTypesResponse) => {
    const loadingType = mapLoadingTypeEnum(loadingTypesResponse);
    freight.loadingTypes.push(loadingType);
  });

  if (freight.transportGoodsType === EFreightTransportGoodsType.WASTE) {
    freight.waste = true;
  }

  // TODO delivery notes, call with new query

  // TODO review this, get a more descriptive title (translate outside, or maybe asign title in component,...)
  freight.title = freight.reference;

  // TODO add the temperatures range if it is a refrigerated load
  // comes from old code (NOT UNCOMMENT)
  // if (
  //   Array.isArray(freightDetailedResponse.transportInfoPayload.transportTemperatureRanges) &&
  //   freightDetailedResponse.transportInfoPayload.transportTemperatureRanges.length > 0
  // ) {
  //   const temperatureRange = freightDetailedResponse.transportInfoPayload.transportTemperatureRanges[0];
  //   const [min, max] = Object.entries(temperatureRange)[0];
  //   freight.temperatureMin = Number(min);
  //   freight.temperatureMax = Number(max);
  //   freight.temperatureUnit = freightDetailedResponse.transportInfoPayload.transportTemperatureUnit || 'CELSIUS';
  // }

  return freight;
}
